import { Component, EventEmitter, Input, Output } from '@angular/core';

import { MaterialModule } from '../../../../shared/theming/material/material.module';
import {
  AnalyticsData,
  isDefaultAnalyticsData,
  isCustomAnalyticsData,
} from '../../../../shared/models/analytics.model';

@Component({
  selector: 'app-analytics-widget',
  standalone: true,
  imports: [MaterialModule],
  templateUrl: './analytics-widget.component.html',
  styleUrls: ['./analytics-widget.component.scss'],
})
export class AnalyticsWidgetComponent {
  @Input() data: AnalyticsData | null = null;
  @Input() isLoading = false;

  @Output() fetchImageData = new EventEmitter<
    'lastDay' | 'lastWeek' | 'custom'
  >();

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatDateRange(startDate: string, endDate: string): string {
    return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
  }

  formatNumber(num?: number | null, isPercentage?: boolean): string {
    const safeNumber = num ?? 0;

    if (isPercentage) {
      return (safeNumber / 100).toLocaleString('en-US', {
        style: 'percent',
      });
    }

    return safeNumber.toLocaleString();
  }

  formatAverage(num?: number | null): string {
    return (num ?? 0).toFixed(2);
  }

  isDefaultAnalytics(): boolean {
    return this.data ? isDefaultAnalyticsData(this.data) : false;
  }

  isCustomAnalytics(): boolean {
    return this.data ? isCustomAnalyticsData(this.data) : false;
  }

  getDefaultData(): any {
    if (this.isDefaultAnalytics() && this.data) {
      return this.data as any;
    }
    return null;
  }

  getCustomData(): any {
    if (this.isCustomAnalytics() && this.data) {
      return this.data as any;
    }
    return null;
  }

  getTextPercentage(metrics?: {
    textBasedInteractions?: number;
    voiceToVoiceInteractions?: number;
    totalInteractions?: number;
  }): number {
    const total =
      metrics?.totalInteractions ??
      (metrics?.textBasedInteractions ?? 0) +
        (metrics?.voiceToVoiceInteractions ?? 0);

    if (total === 0) {
      return 0;
    }

    return ((metrics?.textBasedInteractions ?? 0) / total) * 100;
  }

  getVoicePercentage(metrics?: {
    textBasedInteractions?: number;
    voiceToVoiceInteractions?: number;
    totalInteractions?: number;
  }): number {
    const total =
      metrics?.totalInteractions ??
      (metrics?.textBasedInteractions ?? 0) +
        (metrics?.voiceToVoiceInteractions ?? 0);

    if (total === 0) {
      return 0;
    }

    return ((metrics?.voiceToVoiceInteractions ?? 0) / total) * 100;
  }

  getProductTypeEntries(): Array<{
    type: string;
    count: number;
    percentage: number;
    imageCount: number;
  }> {
    if (this.isCustomAnalytics() && this.data) {
      const customData = this.data as any;
      const breakdown = customData.productTypeBreakdown || {};
      const imagesByProductType = customData.imagesByProductType || {};
      return Object.entries(breakdown).map(([type, data]: [string, any]) => ({
        type,
        count: data.count,
        percentage: data.percentage,
        imageCount: imagesByProductType[type] || 0,
      }));
    }
    return [];
  }
}
